import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Reveal, SectionHeading } from "./Reveal";

const TIERS = {
  breeze: {
    name: "Breeze Club",
    price: "Free",
    cadence: "Forever",
    tagline: "Our open table. Join in one step.",
    perks: ["First access to new releases", "Seasonal newsletter & dates", "10% off seated tastings", "Invitations to public events"],
    cta: "Join Free",
    testid: "club-tier-breeze-join-btn",
  },
  reserve: {
    name: "Cellar Reserve",
    price: "$150",
    cadence: "Per Quarter",
    tagline: "Six hand-selected bottles, four times a year.",
    perks: [
      "6 bottles each quarter, chosen by Javier",
      "Cellar pick-up party invitations",
      "Complimentary seated tastings for two",
      "Priority seats at farm dinners",
      "Member-only library wines",
    ],
    cta: "Reserve My Allocation",
    testid: "club-tier-cellar-join-btn",
  },
};

const JoinModal = ({ tier, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const t = TIERS[tier];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/club/join", { ...form, tier });
      if (tier === "reserve") {
        setStep("redirect");
        const res = await api.post("/payments/checkout", {
          lookup_key: "cellar_reserve_quarterly",
          origin_url: window.location.origin,
          member_id: data.member.id,
        });
        window.location.href = res.data.checkout_url;
        return;
      }
      setStep("done");
      toast.success("Welcome to the Breeze Club.");
    } catch (err) {
      setError(formatApiError(err));
      setStep("form");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-[#0A0908]/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={step === "redirect" ? undefined : onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#141210] border border-[rgba(212,175,55,0.25)] p-8 relative"
      >
        {step !== "redirect" && (
          <button data-testid="club-modal-close" onClick={onClose} className="absolute top-4 right-4 text-[#6E685E] hover:text-[#F4F1EA]" aria-label="Close">
            <X size={18} />
          </button>
        )}

        <p className="overline-label">{t.name}</p>
        <h3 className="font-serif-display text-3xl font-light text-[#F4F1EA] mt-3">
          {step === "form" && "Join the club."}
          {step === "redirect" && "Securing your checkout…"}
          {step === "done" && "Welcome in."}
        </h3>

        {step === "form" && (
          <form data-testid="club-signup-modal-form" onSubmit={submit} className="mt-6 flex flex-col gap-4">
            <input
              data-testid="club-form-input-name"
              required
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-[#0A0908] border border-[rgba(212,175,55,0.2)] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-[#F4F1EA] placeholder:text-[#6E685E] transition-colors"
            />
            <input
              data-testid="club-form-input-email"
              required
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-[#0A0908] border border-[rgba(212,175,55,0.2)] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-[#F4F1EA] placeholder:text-[#6E685E] transition-colors"
            />
            <input
              data-testid="club-form-input-phone"
              placeholder="Phone (optional)"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="bg-[#0A0908] border border-[rgba(212,175,55,0.2)] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-[#F4F1EA] placeholder:text-[#6E685E] transition-colors"
            />
            {error && <p data-testid="club-form-error" className="text-sm text-[#C86D51]">{error}</p>}
            <button
              data-testid="club-form-submit-btn"
              disabled={busy}
              className="mt-2 font-mono text-xs uppercase tracking-[0.25em] bg-[#D4AF37] text-[#0A0908] py-4 hover:bg-[#F4F1EA] transition-colors disabled:opacity-50"
            >
              {busy ? "Joining…" : tier === "reserve" ? "Continue to Secure Checkout" : "Join the Breeze Club"}
            </button>
            {tier === "reserve" && (
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E685E] text-center">
                $150 / quarter · Secure card payment · Cancel anytime
              </p>
            )}
          </form>
        )}

        {step === "redirect" && (
          <div data-testid="club-checkout-redirect" className="mt-8 flex flex-col items-center gap-4 py-6">
            <Loader2 size={28} className="text-[#D4AF37] animate-spin" />
            <p className="text-sm text-[#A39E93] text-center leading-relaxed">
              Taking you to our secure payment page to complete your first quarterly payment of $150.
            </p>
          </div>
        )}

        {step === "done" && (
          <div className="mt-6">
            <p data-testid="club-join-success" className="text-sm text-[#A39E93] leading-relaxed">
              You're on the list. First access to releases, seasonal dates, and tasting privileges land in your inbox soon.
            </p>
            <button
              data-testid="club-done-close-btn"
              onClick={onClose}
              className="mt-6 w-full font-mono text-xs uppercase tracking-[0.25em] border border-[#D4AF37] text-[#D4AF37] py-4 hover:bg-[#D4AF37] hover:text-[#0A0908] transition-colors"
            >
              Back to the Estate
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

const TierCard = ({ tierKey, featured, onJoin }) => {
  const t = TIERS[tierKey];
  return (
    <div
      data-testid={`club-tier-card-${tierKey}`}
      className={`spotlight-card relative p-8 lg:p-10 flex flex-col ${
        featured
          ? "bg-[#1A1714] border border-[#D4AF37]/50 lg:-translate-y-4"
          : "bg-[#141210] border border-[rgba(212,175,55,0.15)]"
      }`}
    >
      {featured && (
        <span className="absolute top-0 right-0 font-mono text-[10px] uppercase tracking-[0.25em] bg-[#D4AF37] text-[#0A0908] px-4 py-1.5">
          Most Coveted
        </span>
      )}
      <p className="overline-label">{t.name}</p>
      <div className="mt-6 flex items-baseline gap-2">
        <span className="font-serif-display text-5xl lg:text-6xl font-light text-[#F4F1EA]">{t.price}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E685E]">{t.cadence}</span>
      </div>
      <p className="text-sm text-[#A39E93] mt-3">{t.tagline}</p>
      <div className="gold-hairline my-8" />
      <ul className="flex flex-col gap-3 flex-1">
        {t.perks.map((p) => (
          <li key={p} className="flex items-start gap-3 text-sm text-[#A39E93]">
            <Check size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
            {p}
          </li>
        ))}
      </ul>
      <button
        data-testid={t.testid}
        onClick={onJoin}
        className={`mt-10 font-mono text-xs uppercase tracking-[0.25em] py-4 transition-colors duration-300 ${
          featured
            ? "bg-[#D4AF37] text-[#0A0908] hover:bg-[#F4F1EA]"
            : "border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0908]"
        }`}
      >
        {t.cta}
      </button>
    </div>
  );
};

export const Club = () => {
  const [joinTier, setJoinTier] = useState(null);

  return (
    <section id="club" data-testid="club-section" className="relative py-28 lg:py-40 bg-[#141210]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <SectionHeading
          overline="The Wine Club"
          title="Two doors into the cellar."
          className="text-center mx-auto max-w-2xl mb-6"
        />
        <Reveal delay={0.2}>
          <p className="text-center text-sm text-[#6E685E] max-w-xl mx-auto mb-16 lg:mb-20">
            Membership starts free. When you're ready, step up to Cellar Reserve —
            six bottles a quarter, $150, chosen by our winemaker.
          </p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6 lg:gap-10 items-start">
          <Reveal>
            <TierCard tierKey="breeze" onJoin={() => setJoinTier("breeze")} />
          </Reveal>
          <Reveal delay={0.15}>
            <TierCard tierKey="reserve" featured onJoin={() => setJoinTier("reserve")} />
          </Reveal>
        </div>
      </div>
      <AnimatePresence>{joinTier && <JoinModal tier={joinTier} onClose={() => setJoinTier(null)} />}</AnimatePresence>
    </section>
  );
};
